<template>
  <Card class="w-full max-w-[600px] mx-auto box-border relative block">
    <template #content>
      <div class="flex flex-col gap-4">
        <!-- Players List -->
        <div
          class="players-list flex flex-col gap-3 md:flex-row md:justify-center md:gap-6 md:items-start"
        >
          <div
            v-for="player in players"
            :key="player.id"
            class="player-item flex items-center gap-3 px-3 py-2 rounded-lg transition-colors duration-150 ease-out md:flex-col md:text-center md:p-4 md:min-w-[120px] md:gap-2"
            :class="{
              'player-item--current':
                player.id === currentPlayerId && !(gamePhase === 'selecting' && currentSelector),
              'player-item--selecting':
                gamePhase === 'selecting' && currentSelector && player.id === currentSelector,
              'player-item--watching':
                gamePhase === 'selecting' && currentSelector && player.id !== currentSelector,
            }"
          >
            <div
              class="player-tile w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold border-2 border-transparent shrink-0 transition-all duration-150 ease-out md:w-10 md:h-10 md:text-base"
              :class="{
                'player-tile--current':
                  player.id === currentPlayerId && !(gamePhase === 'selecting' && currentSelector),
                'player-tile--selecting':
                  gamePhase === 'selecting' && currentSelector && player.id === currentSelector,
                'player-tile--watching':
                  gamePhase === 'selecting' && currentSelector && player.id !== currentSelector,
              }"
              :style="{ backgroundColor: getPlayerColor(player.id) }"
              :aria-label="`${STRINGS.aria.labels.playerProfile} ${player.name} profile`"
            >
              <AppIcon v-if="player.is_bot" name="bot" :size="22" color="white" /><span v-else>{{
                getPlayerInitials(player)
              }}</span>
            </div>
            <span class="flex-1 text-base font-semibold uppercase">
              {{ player.name
              }}<span
                v-if="player.id === currentPlayerId"
                class="font-normal text-gray-600 text-sm normal-case"
                >{{ ' ' + STRINGS.status.player.youLabel }}</span
              >
            </span>

            <!-- Show selection phase indicators -->
            <span
              v-if="gamePhase === 'selecting' && currentSelector && player.id === currentSelector"
              class="role-label role-label--selector"
              :aria-label="STRINGS.aria.labels.currentSelector"
            >
              {{ STRINGS.status.selection.selecting }}
            </span>
            <span
              v-else-if="
                gamePhase === 'selecting' && currentSelector && player.id !== currentSelector
              "
              class="role-label role-label--watcher"
            >
              {{ STRINGS.status.selection.watching }}
            </span>

            <!-- Show word-finding phase status indicators -->
            <div
              v-if="gamePhase === 'word-finding' && playerWordStatus[player.id]"
              class="player-status-indicator flex items-center gap-1 px-2 py-1 rounded transition-all duration-150 ease-out"
              :class="{
                [`player-status-indicator--${getStatusKey(playerWordStatus[player.id].status)}`]: true,
                'player-status-indicator--submitted': isSubmitted(
                  playerWordStatus[player.id].status
                ),
              }"
              :aria-label="`${player.name} ${STRINGS.aria.labels.wordStatus} ${getStatusKey(playerWordStatus[player.id].status)}`"
            >
              <!-- Green checkmark for submitted players -->
              <span
                v-if="isSubmitted(playerWordStatus[player.id].status)"
                class="inline-flex items-center justify-center w-4 h-4"
                :aria-label="STRINGS.aria.labels.wordSubmittedStatus"
              >
                <AppIcon name="checkmark" :size="14" color="white" />
              </span>

              <span class="text-xs font-semibold">{{ getPlayerStatusText(player.id) }}</span>
              <span
                v-if="playerWordStatus[player.id].wordLength"
                class="word-length-indicator inline-flex items-center justify-center w-5 h-5 bg-[var(--color-gray-600,#475569)] text-white rounded-full text-xs font-bold ml-1"
                :aria-label="`${STRINGS.aria.labels.wordLength} ${playerWordStatus[player.id].wordLength} letters`"
              >
                {{ playerWordStatus[player.id].wordLength }}
              </span>
            </div>
          </div>
        </div>

        <!-- Status Message -->
        <div
          class="status-message text-center px-4 py-3 rounded-md md:px-6 md:py-4"
          aria-live="polite"
          aria-atomic="true"
          role="status"
        >
          <p
            class="status-primary m-0 mb-1"
            :class="{
              'status-primary--selector':
                gamePhase === 'selecting' && currentSelector && currentSelector === currentPlayerId,
              'status-primary--watcher':
                gamePhase === 'selecting' && currentSelector && currentSelector !== currentPlayerId,
              'status-primary--default': gamePhase !== 'selecting',
            }"
          >
            {{ primaryStatusMessage }}
          </p>
          <p
            v-if="secondaryStatusMessage"
            class="status-secondary text-sm text-arena-text-dim m-0 italic"
          >
            {{ secondaryStatusMessage }}
          </p>
        </div>
      </div>
    </template>
  </Card>
</template>

<script setup lang="ts">
import { computed, watch, onMounted } from 'vue';
import { STRINGS } from '@/constants/strings';
import Card from 'primevue/card';
import AppIcon from '@/components/ui/icons/AppIcon.vue';
import { useLiveAnnouncements } from '@/composables/useLiveAnnouncements';
import type { Player, SelectionStatus, PlayerSubmissionStatus } from '@/types/generated';
import { reportClientError, ErrorLocation } from '@/telemetry/errors';

interface Props {
  players: Player[];
  currentSelector: string;
  currentPlayerId: string;
  status: SelectionStatus;
  vowelCount?: number;
  consonantCount?: number;
  showConstraints?: boolean;
  gamePhase?: 'selecting' | 'word-finding' | 'ended';
  playerWordStatus?: {
    [playerId: string]: {
      status: PlayerSubmissionStatus;
      wordLength?: number;
      lastActivity?: number;
    };
  };
  gameTransitioning?: boolean;
  timeRemaining?: number;
}

const props = withDefaults(defineProps<Props>(), {
  vowelCount: 0,
  consonantCount: 0,
  showConstraints: true,
  gamePhase: 'selecting',
  playerWordStatus: () => ({}),
  gameTransitioning: false,
  timeRemaining: 0,
});

// Live announcements composable
const liveAnnouncements = useLiveAnnouncements();

// Returns a CSS-class-safe key for a PlayerSubmissionStatus value
const getStatusKey = (status: PlayerSubmissionStatus): string => {
  if (typeof status === 'object') return 'submitted';
  switch (status) {
    case 'WaitingToStart':
      return 'waiting';
    case 'Typing':
      return 'typing';
    case 'Disconnected':
      return 'disconnected';
    default: {
      const _exhaustive: never = status;
      reportClientError(
        new Error(`Unknown PlayerSubmissionStatus variant: ${_exhaustive}`),
        ErrorLocation.PlayerStatusUnknownVariant
      );
      return 'unknown';
    }
  }
};

// Returns true if the player has submitted
const isSubmitted = (status: PlayerSubmissionStatus): boolean => typeof status !== 'string';

// Get player tile color based on player ID
const getPlayerColor = (playerId: string) => {
  // Create consistent colors for each player based on their position in the players array
  const playerIndex = props.players.findIndex(p => p.id === playerId);
  const colors = [
    'var(--color-energy-primary)', // Player 1: Gold
    'var(--color-energy-secondary)', // Player 2: Steel Blue
  ];
  return colors[playerIndex] || 'var(--color-energy-neutral)';
};

// Get player initial letter for avatar display
const getPlayerInitials = (player: Player) => player.name.charAt(0).toUpperCase();

// Get player status text for word-finding phase
const getPlayerStatusText = (playerId: string) => {
  const status = props.playerWordStatus?.[playerId]?.status;
  if (status === undefined) return '';

  if (typeof status === 'object') return STRINGS.status.player.submitted;

  switch (status) {
    case 'WaitingToStart':
      return STRINGS.status.player.ready;
    case 'Typing':
      return STRINGS.status.player.typing;
    case 'Disconnected':
      return STRINGS.status.player.offline;
    default: {
      const _exhaustive: never = status;
      reportClientError(
        new Error(`Unknown PlayerSubmissionStatus variant: ${_exhaustive}`),
        ErrorLocation.PlayerStatusUnknownVariant
      );
      return '';
    }
  }
};

// Get current selector name for messages
const getCurrentSelectorName = () => {
  const selector = props.players.find(p => p.id === props.currentSelector);
  return selector ? selector.name : STRINGS.status.player.opponentFallback;
};

// Compute primary status message
const primaryStatusMessage = computed(() => {
  // Word-finding phase messages
  if (props.gamePhase === 'word-finding') {
    const currentPlayerStatus = props.playerWordStatus?.[props.currentPlayerId];
    if (currentPlayerStatus && isSubmitted(currentPlayerStatus.status)) {
      // Check if opponent has also submitted
      const opponentStatus = Object.values(props.playerWordStatus || {}).find(
        s => s !== currentPlayerStatus
      );
      if (opponentStatus && isSubmitted(opponentStatus.status)) {
        return STRINGS.status.wordFinding.bothReady;
      }
      return STRINGS.status.wordFinding.waitingForOpponent;
    }
    return STRINGS.status.wordFinding.findWords;
  }

  // Selection phase messages (active voice, less prominent)
  if (typeof props.status === 'string') {
    switch (props.status) {
      case 'WaitingForSelection':
        // Handle case where no selector is set yet
        if (!props.currentSelector) {
          return STRINGS.status.selection.waitingForAssignment;
        }
        return props.currentSelector === props.currentPlayerId
          ? STRINGS.status.selection.yourTurn
          : `${getCurrentSelectorName()} ${STRINGS.status.selection.opponentSelecting}`;

      case 'TooManyVowels':
        return STRINGS.status.selection.selectConsonants;

      case 'TooManyConsonants':
        return STRINGS.status.selection.selectVowels;

      case 'SelectionComplete':
        return STRINGS.status.selection.selectionComplete;

      default:
        return STRINGS.status.selection.selectionInProgress;
    }
  } else if (typeof props.status === 'object') {
    // Handle object-type status messages
    if ('WaitingForOpponent' in props.status) {
      return STRINGS.templates.opponentSelectingLetter(props.status.WaitingForOpponent);
    }

    if ('MustSelectMoreVowels' in props.status) {
      const count = props.status.MustSelectMoreVowels;
      return STRINGS.templates.selectMoreVowels(count);
    }

    if ('MustSelectMoreConsonants' in props.status) {
      const count = props.status.MustSelectMoreConsonants;
      return STRINGS.templates.selectMoreConsonants(count);
    }
  }

  return STRINGS.status.selection.selectionInProgress;
});

// Compute secondary status message for additional context
const secondaryStatusMessage = computed(() => {
  // Word-finding phase messages
  if (props.gamePhase === 'word-finding') {
    const currentPlayerStatus = props.playerWordStatus?.[props.currentPlayerId];
    if (currentPlayerStatus?.status === 'Typing') {
      return STRINGS.status.wordFinding.everySecond;
    }
    if (currentPlayerStatus && isSubmitted(currentPlayerStatus.status)) {
      return STRINGS.status.wordFinding.greatJob;
    }
    return STRINGS.status.wordFinding.typeBest;
  }

  // Selection phase messages (existing logic)
  if (props.currentSelector && props.currentSelector === props.currentPlayerId) {
    return STRINGS.status.selection.chooseWisely;
  }

  // Show constraint hints for non-selector
  if (
    typeof props.status === 'string' &&
    (props.status === 'TooManyVowels' || props.status === 'TooManyConsonants')
  ) {
    return STRINGS.status.selection.balanceStrategy;
  }

  return null;
});

// Watch for turn changes and announce them
watch(
  () => props.currentSelector,
  (newSelector, oldSelector) => {
    if (oldSelector && newSelector !== oldSelector) {
      const selectorName = getCurrentSelectorName();
      const isYourTurn = newSelector === props.currentPlayerId;
      liveAnnouncements.announceTurnChange(selectorName, isYourTurn);
    }
  },
  { immediate: false }
);

// Watch for status changes and announce important ones
watch(
  () => props.status,
  (newStatus, oldStatus) => {
    if (oldStatus && newStatus !== oldStatus) {
      // Announce status changes that require user attention
      if (typeof newStatus === 'string') {
        switch (newStatus) {
          case 'TooManyVowels':
            liveAnnouncements.announceError(
              `Too many vowels selected. Please ${STRINGS.status.selection.selectConsonants.toLowerCase()}.`
            );
            break;
          case 'TooManyConsonants':
            liveAnnouncements.announceError(
              `Too many consonants selected. Please ${STRINGS.status.selection.selectVowels.toLowerCase()}.`
            );
            break;
          case 'SelectionComplete':
            liveAnnouncements.announceSuccess(
              `${STRINGS.status.selection.selectionComplete} Moving to game phase.`
            );
            break;
        }
      } else if (typeof newStatus === 'object') {
        if ('MustSelectMoreVowels' in newStatus) {
          const count = newStatus.MustSelectMoreVowels;
          liveAnnouncements.announce(STRINGS.templates.mustSelectVowels(count), {
            politeness: 'assertive',
            priority: 'high',
          });
        } else if ('MustSelectMoreConsonants' in newStatus) {
          const count = newStatus.MustSelectMoreConsonants;
          liveAnnouncements.announce(STRINGS.templates.mustSelectConsonants(count), {
            politeness: 'assertive',
            priority: 'high',
          });
        }
      }
    }
  },
  { deep: true }
);

// Watch for vowel/consonant count changes and announce progress
watch(
  [() => props.vowelCount, () => props.consonantCount],
  ([newVowels, newConsonants], [oldVowels, oldConsonants]) => {
    if (oldVowels !== undefined && oldConsonants !== undefined) {
      if (newVowels > oldVowels) {
        liveAnnouncements.announceProgress(STRINGS.templates.vowelProgress(newVowels));
      }
      if (newConsonants > oldConsonants) {
        liveAnnouncements.announceProgress(STRINGS.templates.consonantProgress(newConsonants));
      }
    }
  }
);

// Initialize live announcements on mount
onMounted(() => {
  liveAnnouncements.createLiveRegions();
});
</script>

<style scoped>
.player-item--current {
  background: rgba(201, 168, 76, 0.1);
  border: 1px solid rgba(201, 168, 76, 0.2);
}

.player-item--selecting {
  background: rgba(201, 168, 76, 0.12);
  border: 1px solid rgba(201, 168, 76, 0.4);
  box-shadow: 0 0 12px rgba(201, 168, 76, 0.15);
}

.player-item--watching {
  background: rgba(138, 126, 106, 0.06);
  border: 1px solid rgba(138, 126, 106, 0.15);
}

.player-tile {
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.player-tile--current {
  border-color: white;
  box-shadow: 0 0 0 1px var(--color-arena-border, #3d3428);
}

.player-tile--selecting {
  border-color: var(--color-energy-primary, #c9a84c);
  position: relative;
}

/* Glow lives on a layer with a static box-shadow whose opacity is animated
   (compositor-friendly) instead of animating box-shadow on the tile itself. */
.player-tile--selecting::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 50%;
  box-shadow: 0 0 16px rgba(201, 168, 76, 0.5);
  pointer-events: none;
  animation: selector-glow 2s ease-in-out infinite;
}

.player-tile--watching {
  border-color: var(--color-arena-border, #3d3428);
  opacity: 0.7;
}

.role-label {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 0.25rem 0.5rem;
  border-radius: 0.125rem;
}

.role-label--selector {
  background: rgba(201, 168, 76, 0.15);
  color: var(--color-energy-primary, #c9a84c);
  border: 1px solid rgba(201, 168, 76, 0.3);
}

.role-label--watcher {
  background: rgba(138, 126, 106, 0.1);
  color: var(--color-arena-text-dim);
  border: 1px solid rgba(138, 126, 106, 0.2);
}

.status-primary--selector {
  color: var(--color-energy-primary, #c9a84c);
  font-size: 1.125rem;
  font-weight: 600;
}

.status-primary--watcher {
  color: var(--color-arena-text-bright);
  font-size: 1rem;
  font-weight: 500;
}

.status-primary--default {
  font-size: 1rem;
  font-weight: 500;
  color: var(--color-arena-text-muted);
}

.player-status-indicator--submitted {
  background: rgba(201, 168, 76, 0.1);
  border: 1px solid rgba(201, 168, 76, 0.3);
}

@keyframes selector-glow {
  0%,
  100% {
    opacity: 0.4;
  }
  50% {
    opacity: 1;
  }
}

@media screen and (min-width: 768px) {
  .status-primary--selector {
    font-size: 1.25rem;
  }

  .status-primary--watcher {
    font-size: 1.125rem;
  }

  .status-primary--default {
    font-size: 1.125rem;
  }
}

@media (prefers-contrast: high) {
  .player-item--current {
    background: var(--color-energy-primary, #c9a84c);
    color: white;
  }

  .role-label--selector,
  .role-label--watcher {
    border-width: 2px;
  }

  .player-status-indicator--submitted,
  .player-status-indicator--waiting,
  .player-status-indicator--typing,
  .player-status-indicator--disconnected {
    border: 2px solid currentColor;
  }
}

@media (prefers-reduced-motion: reduce) {
  .player-tile--selecting::after {
    animation: none;
    opacity: 0.7;
  }
}
</style>
